import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import picto_copy from 'images/picto_copy.svg';
import picto_download from 'images/picto_download.svg';
import picto_unwrap from 'images/picto_unwrap.svg';
import picto_roll_up from 'images/picto_roll_up.svg';
import { useTranslation } from 'react-i18next';

const Container = styled.section`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 18px;
	border: 1px solid ${props => props.theme.lightGray};
	border-radius: 10px;
	background: white;
	box-shadow: 0 4px 18px rgba(0, 70, 133, 0.06);

	h3 {
		margin: 0 !important;
	}
`;

const Row = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: 12px;
	width: 100%;

	@media (min-width: 960px) {
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
	}
`;

const Actions = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;

	a, button {
		display: inline-flex;
		align-items: center;
		gap: 7px;
		min-height: 36px;
		padding: 7px 10px;
		border: 1px solid ${props => props.theme.lightGray};
		border-radius: 6px;
		background: white;
		color: ${props => props.theme.darkBlue} !important;
		font: 16px "PT Sans", sans-serif;
		text-decoration: none;
		cursor: pointer;

		&:hover, &:focus-visible {
			border-color: ${props => props.theme.darkBlue};
			background: #f3f8fc;
		}

		&:disabled, &[aria-disabled="true"] {
			opacity: 0.55;
			cursor: default;
		}
	}

	img {
		width: 20px;
		height: 20px;
	}
`;

const Status = styled.div`
	min-height: 20px;
	color: #a2001d;
	font: 14px "PT Sans", sans-serif;
`;

const CodeViewport = styled.div`
	position: ${props => props.$fullscreen ? 'fixed' : 'relative'};
	inset: ${props => props.$fullscreen ? '20px' : 'auto'};
	z-index: ${props => props.$fullscreen ? 10000 : 'auto'};
	height: ${props => props.$fullscreen ? 'calc(100vh - 40px)' : props.$expanded ? 'auto' : 'calc(15.5em + 20px)'};
	overflow: auto;
	border: 1px solid ${props => props.theme.placeholderColor};
	border-radius: 8px;
	background: #f7fafc;
	box-shadow: ${props => props.$fullscreen ? '0 12px 50px rgba(0, 33, 61, 0.35)' : 'none'};
	font: 14px/1.55 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
	tab-size: 2;
`;

const Code = styled.code`
	display: block;
	width: ${props => props.$wrap ? '100%' : 'max-content'};
	min-width: 100%;
	padding: 10px 0;
`;

const CodeLine = styled.span`
	display: grid;
	grid-template-columns: 56px minmax(0, 1fr);

	&:hover {
		background: #edf5fb;
	}
`;

const LineNumber = styled.span`
	display: block;
	padding: 0 12px;
	border-right: 1px solid #dbe8f2;
	color: #7890a5;
	text-align: right;
	user-select: none;
`;

const LineContent = styled.span`
	display: block;
	padding: 0 14px;
	color: #263746;
	white-space: ${props => props.$wrap ? 'pre-wrap' : 'pre'};
	word-break: ${props => props.$wrap ? 'break-word' : 'normal'};
`;

const XmlTag = styled.span`
	color: #005a9c;
`;

const XmlDeclaration = styled.span`
	color: #6f42c1;
`;

const XmlValue = styled.span`
	color: #a04b00;
`;

const StatePanel = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	min-height: 180px;
	padding: 24px;
	border: 1px solid ${props => props.theme.lightGray};
	border-radius: 8px;
	background: #f7fafc;
	color: ${props => props.$error ? '#a2001d' : props.theme.darkBlue};
	font-family: "PT Sans", sans-serif;
	text-align: center;

	button {
		padding: 8px 14px;
		border: 1px solid ${props => props.theme.darkBlue};
		border-radius: 6px;
		background: white;
		color: ${props => props.theme.darkBlue};
		cursor: pointer;
	}
`;

const FullscreenClose = styled.button`
	position: fixed;
	top: 30px;
	right: 30px;
	z-index: 10001;
	max-width: calc(100vw - 60px);
	padding: 8px 12px;
	border: 1px solid ${props => props.theme.darkBlue};
	border-radius: 6px;
	background: white;
	color: ${props => props.theme.darkBlue};
	font: 16px "PT Sans", sans-serif;
	cursor: pointer;

	@media (max-width: 480px) {
		top: 28px;
		right: 28px;
		max-width: calc(100vw - 56px);
	}
`;

const renderXmlLine = (line, lineIndex) => line.split(/(<[^>]+>)/g).map((part, partIndex) => {
	if (!part.startsWith('<')) return part;

	const Tag = part.startsWith('<?') || part.startsWith('<!') ? XmlDeclaration : XmlTag;
	return (
		<Tag key={`${lineIndex}-${partIndex}`}>
			{part.split(/("[^"]*"|'[^']*')/g).map((token, tokenIndex) => (
				token.startsWith('"') || token.startsWith('\'')
					? <XmlValue key={tokenIndex}>{token}</XmlValue>
					: token
			))}
		</Tag>
	);
});

export const Example = ({url, title}) => {
	const { t } = useTranslation();
	const [ loading, setLoading ] = useState(true);
	const [ loadError, setLoadError ] = useState('');
	const [ feedback, setFeedback ] = useState({ text: '', error: false });
	const [ isOpened, setIsOpened ] = useState(false);
	const [ isFullscreen, setIsFullscreen ] = useState(false);
	const [ wrap, setWrap ] = useState(false);
	const [ data, setData ] = useState('');
	const [ downloadUrl, setDownloadUrl ] = useState('');
	const [ reloadKey, setReloadKey ] = useState(0);

	useEffect(() => {
		const controller = new AbortController();

		setLoading(true);
		setLoadError('');
		setFeedback({ text: '', error: false });
		setData('');

		axios.get(url, { signal: controller.signal })
			.then(response => setData(String(response.data)))
			.catch(requestError => {
				if (requestError.code !== 'ERR_CANCELED') {
					setLoadError(t('Не удалось загрузить пример. Проверьте соединение и повторите попытку.'));
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});

		return () => controller.abort();
	}, [url, reloadKey, t]);

	useEffect(() => {
		if (!data) {
			setDownloadUrl('');
			return undefined;
		}

		const objectUrl = URL.createObjectURL(new Blob([data], { type: 'application/xml;charset=utf-8' }));
		setDownloadUrl(objectUrl);
		return () => URL.revokeObjectURL(objectUrl);
	}, [data]);

	useEffect(() => {
		if (!isFullscreen) return undefined;

		const onKeyDown = event => {
			if (event.key === 'Escape') setIsFullscreen(false);
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isFullscreen]);

	useEffect(() => {
		if (!feedback.text || feedback.error) return undefined;
		const timer = setTimeout(() => setFeedback({ text: '', error: false }), 2000);
		return () => clearTimeout(timer);
	}, [feedback]);

	const onCopy = async () => {
		try {
			await navigator.clipboard.writeText(data);
			setFeedback({ text: t('Скопировано'), error: false });
		} catch {
			setFeedback({
				text: t('Не удалось скопировать текст. Разрешите доступ к буферу обмена и повторите попытку.'),
				error: true,
			});
		}
	};

	const lines = data.split(/\r?\n/);
	const fileName = decodeURIComponent(url.split('/').pop() || 'example.xml');

	return (
		<Container aria-busy={loading}>
			<Row>
				<h3>{title}</h3>
				<Actions role="group" aria-label={t('Действия с примером')}>
					<a href={downloadUrl || undefined} download={fileName} aria-disabled={!downloadUrl} onClick={event => !downloadUrl && event.preventDefault()}>
						<img src={picto_download} alt="" /> {t('Скачать')}
					</a>
					<button type="button" onClick={onCopy} disabled={loading || Boolean(loadError)}>
						<img src={picto_copy} alt="" /> {feedback.text && !feedback.error ? feedback.text : t('Копировать')}
					</button>
					<button type="button" onClick={() => setWrap(value => !value)} disabled={loading || Boolean(loadError)}>
						{wrap ? t('Не переносить строки') : t('Переносить строки')}
					</button>
					<button type="button" onClick={() => setIsOpened(value => !value)} disabled={loading || Boolean(loadError)}>
						<img src={isOpened ? picto_roll_up : picto_unwrap} alt="" />
						{isOpened ? t('Свернуть') : t('Развернуть')}
					</button>
					<button type="button" onClick={() => setIsFullscreen(value => !value)} disabled={loading || Boolean(loadError)}>
						{isFullscreen ? t('Закрыть полный экран') : t('На весь экран')}
					</button>
				</Actions>
			</Row>

			{feedback.error && <Status role="status">{feedback.text}</Status>}

			{loading && <StatePanel role="status">{t('Загрузка примера…')}</StatePanel>}

			{!loading && loadError && (
				<StatePanel $error>
					<span>{loadError}</span>
					<button type="button" onClick={() => setReloadKey(value => value + 1)}>{t('Повторить')}</button>
				</StatePanel>
			)}

			{!loading && !loadError && (
				<CodeViewport $expanded={isOpened} $fullscreen={isFullscreen}>
					{isFullscreen && (
						<FullscreenClose type="button" onClick={() => setIsFullscreen(false)}>
							{t('Закрыть полный экран')}
						</FullscreenClose>
					)}
					<Code $wrap={wrap} aria-label={title}>
						{lines.map((line, index) => (
							<CodeLine key={index}>
								<LineNumber aria-hidden="true">{index + 1}</LineNumber>
								<LineContent $wrap={wrap}>{renderXmlLine(line, index)}{'\n'}</LineContent>
							</CodeLine>
						))}
					</Code>
				</CodeViewport>
			)}
		</Container>
	);
};
